import { css } from 'astroturf';
import React, { useEffect, useRef, useState } from 'react';
import { Close } from 'react-bytesize-icons';

const styles = css`
  .dialog {
    padding: 0;
    border: 0;
    background: transparent;
    width: min(92vw, 1400px);
    max-width: none;
    max-height: calc(100svh - 120px);
    aspect-ratio: 16 / 9;
    overflow: visible;

    opacity: 0;
    scale: 0.95;

    @media (prefers-reduced-motion: no-preference) {
      transition:
        opacity 0.25s ease-out,
        scale 0.25s ease-out,
        overlay 0.25s ease-out,
        display 0.25s ease-out;
      transition-behavior: allow-discrete;
    }

    &[open] {
      opacity: 1;
      scale: 1;
    }

    @starting-style {
      &[open] {
        opacity: 0;
        scale: 0.95;
      }
    }

    &::backdrop {
      background: rgba(10, 21, 48, 0);
      backdrop-filter: blur(0px);
      -webkit-backdrop-filter: blur(0px);

      @media (prefers-reduced-motion: no-preference) {
        transition:
          background-color 0.25s ease-out,
          backdrop-filter 0.25s ease-out,
          -webkit-backdrop-filter 0.25s ease-out,
          overlay 0.25s ease-out,
          display 0.25s ease-out;
        transition-behavior: allow-discrete;
      }
    }

    &[open]::backdrop {
      background: rgba(10, 21, 48, 0.7);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }

    @starting-style {
      &[open]::backdrop {
        background: rgba(10, 21, 48, 0);
        backdrop-filter: blur(0px);
      }
    }
  }

  .inner {
    position: relative;

    & button.closebtn {
      margin: 0;
      padding: 0;
      border: 0;
      background: none;
      cursor: pointer;
      position: absolute;
      right: 0;
      top: -2.75rem;
      color: #fff;

      & svg {
        display: block;
      }
    }
  }

  .videowrap {
    position: relative;
    width: 100%;
    padding-top: min(56.25%, calc(100svh - 120px));
    background: #000;
    border-radius: 10px;
    overflow: hidden;

    & iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 0;
    }
  }

  .cover {
    position: absolute;
    inset: 0;
    background: #000;
    opacity: 1;
    pointer-events: none;
    transition: opacity 0.4s ease-out;
  }

  .coverHidden {
    opacity: 0;
  }
`;

const VideoModal = ({ videoId, title, open, onClose }) => {
  const dialogRef = useRef(null);
  // Stays true through the closing transition so the video doesn't vanish before the dialog has faded out.
  const [mounted, setMounted] = useState(open);
  // Masks the YouTube player's own play/pause icon flash while autoplay kicks in.
  const [showCover, setShowCover] = useState(open);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (open) {
      setMounted(true);
      setShowCover(true);
      if (!dialog.open) dialog.showModal();
      const coverTimeout = setTimeout(() => setShowCover(false), 1200);
      return () => clearTimeout(coverTimeout);
    }

    if (dialog.open) dialog.close();

    const handleTransitionEnd = (e) => {
      if (e.target === dialog) setMounted(false);
    };
    dialog.addEventListener('transitionend', handleTransitionEnd);
    return () => dialog.removeEventListener('transitionend', handleTransitionEnd);
  }, [open]);

  return (
    // eslint-disable-next-line react/no-unknown-property -- `closedby` is a real dialog attribute ESLint doesn't know yet
    <dialog closedby="any" ref={dialogRef} className={styles.dialog} aria-label={title} onClose={onClose}>
      <div className={styles.inner}>
        <button type="button" className={styles.closebtn} aria-label="Close video" onClick={onClose} data-tip="Close">
          <Close width={32} height={32} />
        </button>
        <div className={styles.videowrap}>
          {mounted && (
            <iframe
              title={title}
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&controls=0&modestbranding=1&rel=0&iv_load_policy=3`}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
          <div className={[styles.cover, !showCover && styles.coverHidden].join(' ')} />
        </div>
      </div>
    </dialog>
  );
};

export default VideoModal;
